"use client";

import { useRef, useState, useEffect } from 'react';
import CustomImage from '@/components/image';
import Button from '@/components/button';
import Modal from '@/components/modal';
import Video from '@/components/video';
import styles from './_hcp-downtitration.module.scss';
import Link from 'next/link';
import PageAnchor from '@/components/page-anchor';
import getOptimizedImageUrl from '@/util/getOptimizedImageUrl';

export default function HcpDosingDowntitration() {
  const modalRef = useRef(null);
  const videoRef = useRef(null);
  const gcDoseModalRef = useRef(null);
  const stressDosingModalRef = useRef(null);
  const carouselRef = useRef(null);
  const [slideNumber, setSlideNumber] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showSymptoms, setShowSymptoms] = useState(true);
  const [carouselHeight, setCarouselHeight] = useState('243px');
  const [videoPlaying, setVideoPlaying] = useState(false);


  const transcript = (
    <div>
      <p><strong>Length: 04:30</strong></p>
      <p><strong>AMRIT BHANGOO, MD:</strong> When we start CRENESSITY, four weeks later, we are doing androstenedione levels.</p>
      <p>And we are looking for a reduction. And then we can do the next step, which would be a glucocorticoid dose reduction after that.</p>
      <p><strong>RUN YU, MD/PHD:</strong> With CRENESSITY, we can directly manage androgens, lower steroid doses, or do both.</p>
      <p>I gradually lower the glucocorticoid dose. In adults, I would do 5 milligrams per month dose reduction.</p>
      <p>We discuss that with CRENESSITY, you still need glucocorticoid….and I encourage my patient to connect with me if the patient feels any side effects of too quick dose reduction such as fatigue, nausea, or feeling dizzy, light-headed.</p>
      <p>I also tell my patients that the most common side effects adult patients experienced in the CRENESSITY clinical trial were fatigue, headache, dizziness, arthralgia, also called the joint pain, back pain, decreased appetite, and myalgia, also called muscle pain.</p>
      <p>And if they do well with this dose reduction, while they&rsquo;re on CRENESSITY, I&rsquo;ll further reduce their dose by another 5 milligrams, then monitor them closely for another month until I reach a dose that I believe is close to a physiological dose.</p>
      <p><strong>AMRIT BHANGOO, MD:</strong> In pediatric patients….if somebody is at 15 milligrams per meter square, you will go down to 13 milligrams per meter square and do the laboratory evaluation, see them back again, see how they are doing, how they tolerate the medication.</p>
      <p>I let them know that the most common side effects pediatric patients experienced in the CRENESSITY clinical trial were headache, abdominal pain, or stomach pain, fatigue, nasal congestion, and epistaxis, or nosebleeds. And if androstenedione levels are in a good range, the patient is tolerating without any complications, especially, you know, checking for fatigue, you know, the symptoms of steroid withdrawal….weakness, tiredness, and even severe headaches….I would go down another 2 milligrams per meter square.</p>
      <p><strong>HEIDI SHEA, MD:</strong> So I agree with that….Dr. Bhangoo, that the two, especially in the kids, the 2 milligrams per meter squared reduction gives you time to sort of assess what have you accomplished, where are things going. In the adult world, though….a lot of times they&rsquo;re on that longer acting at bedtime….so my goal has been to start weaning down that bedtime.</p>
      <p><strong>AMRIT BHANGOO, MD:</strong> Yeah, I fully agree with that. The bedtime dose was the first dose that I decreased on my patients.</p>
      <p><strong>HEIDI SHEA, MD:</strong> If they&rsquo;re on longer acting, maybe we just switch them to an equivalent shorter acting….the folks that I have on dexamethasone….what we talked about was actually switching them to hydrocortisone….this one gentleman in particular, when he went from dexamethasone to hydrocortisone, because he came into my clinic saying, &ldquo;I prefer dex, it&rsquo;s only once a day, I don&rsquo;t want to do anything else.&rdquo; But for the study, he did convert over to hydrocortisone. It was very interesting because he&rsquo;s like, &ldquo;I haven&rsquo;t felt this good on hydrocortisone.&rdquo;</p>
      <p><strong>AMRIT BHANGOO, MD:</strong> I would add, especially in the younger children, they are more at risk for developing adrenal insufficiency. So checking electrolytes in the younger children is, I think, really important, especially when we are decreasing glucocorticoid dosing.</p>
      <p><strong>HEIDI SHEA, MD:</strong> I think it&rsquo;s important in all of them….And also, too, when you&rsquo;re having that discussion about what to expect, you know, I always tell them, if this feels too aggressive, if you have these symptoms, you can always let us know. But then I give them that action plan, go ahead and go back up to the prior dose, give us a call, or go ahead and go up to half the prior dose and see if you can tolerate that better. And I think there&rsquo;s another piece that we haven&rsquo;t quite touched on yet….making sure they know what their stress dosing is.</p>
      <p><strong>HEIDI SHEA, MD:</strong> A lot of folks will come into the clinic, and I&rsquo;ll ask them, &ldquo;What is your emergency plan?&rdquo; &ldquo;Oh, my doctor just told me to double my dose.&rdquo; Well, doubling your dose when you&rsquo;re on a high dose probably does the trick. If you&rsquo;re actually getting them to a physiologic dose, it may not be enough. I just want to make sure they have a smooth transition to whatever their new normal is going to be.</p>
      <p><strong>RUN YU, MD/PHD:</strong> CRENESSITY really allows us to do things we dreamed of doing but we could not do… lower the glucocorticoid dose… give the patient a better management plan.</p>
    </div>
  );

  function handleReadTranscript() {
    setShowTranscript(!showTranscript);
  }

  function handleSymptomsToggle() {
    setShowSymptoms(!showSymptoms);
  }

  function closeAllModals() {
    modalRef.current?.close();
    gcDoseModalRef.current?.close();
    stressDosingModalRef.current?.close();
    setCarouselHeight("auto");
  }

  function handleGcDoseModal() {
    closeAllModals();
    setTimeout(() => gcDoseModalRef.current?.open(), 100);
  }

  function handleStressDosingModal() {
    closeAllModals();
    setTimeout(() => stressDosingModalRef.current?.open(), 100);
    setTimeout(() => updateCarouselHeight(), 150);
  }

  function handleSlideChange(newSlideNumber) {
    if (newSlideNumber < 0 || newSlideNumber > 3) return;
    setSlideNumber(newSlideNumber);
  }

  const updateCarouselHeight = () => {
    if (carouselRef.current) {
      const slides = carouselRef.current.querySelectorAll(`.${styles.carousel_slide_block}`);
      if (slides[slideNumber]) {
        const currentSlideHeight = slides[slideNumber].offsetHeight;
        console.log("currentSlideHeight: ", currentSlideHeight);
        
        setCarouselHeight(`${currentSlideHeight}px`);
      }
    }
  };

  useEffect(() => {
    if (stressDosingModalRef.current?.isOpen) {
      setSlideNumber(0);
    }
  }, [stressDosingModalRef.current?.isOpen]);

  useEffect(() => {
    updateCarouselHeight();
    window.addEventListener('resize', updateCarouselHeight);
    return () => {
      window.removeEventListener('resize', updateCarouselHeight);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideNumber]);

  return (
  <>
    <PageAnchor anchorId="gc-down-titration" />
    <section className={styles.down}>
      <div className={`container ${styles.down_container}`}>
        <div className={styles.down_content}>
          <h2 className={`${styles.down_heading} h1`}>Support your patients taking CRENESSITY through GC down&#8209;titration</h2>
          <p className="mb-4 text-hcp-sailor-blue">Tailor the process to each patient&rsquo;s clinical picture<sup>6</sup></p>
          <p className="text-hcp-charcoal"><strong>When reducing GCs, preparation and communication are key.</strong> Patients may experience withdrawal symptoms—especially with faster reductions—but these symptoms typically improve within a few weeks to a month. Setting expectations early can help maintain adherence to both CRENESSITY and the broader treatment plan. Discuss <a 
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleSymptomsToggle();
            }}
            className="underline text-current cursor-pointer"
            role="button"
            tabIndex={0}
            aria-expanded={showSymptoms}
            aria-controls="symptoms-content"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSymptomsToggle();
              }
            }}
            data-gtm-event-category="Accordion"
            data-gtm-event-action="Click"
            data-gtm-event-label="View common symptoms"
          >common symptoms</a> of GC withdrawal.<sup>6</sup>
          </p>

          {!showSymptoms && (
            <div id="symptoms-content">
              <div className={`${styles.down_purple_container} ${styles.down_purple_container_common_symptoms} !border-0 !bg-white !my-0 tablet:!my-3`}>  
                <ul>
                  <li>General malaise</li>
                  <li>Fatigue</li>
                  <li>Nausea</li>
                  <li>Arthralgia</li>
                  <li>Sleep disturbances</li>
                  <li>Mood changes</li>
                </ul>
              </div>

              <p className="text-hcp-charcoal">The risk of GC withdrawal symptoms is greater with higher-potency GCs (eg, dexamethasone).</p>
              <p className="text-hcp-charcoal">Review CRENESSITY safety in both <Link className="underline text-hcp-charcoal" data-gtm-event-category="Internal Link" data-gtm-event-action="Click" data-gtm-event-label="adult" href="/clinical-information#trial-2-safety">adult</Link> and <Link className="underline" data-gtm-event-category="Internal Link" data-gtm-event-action="Click" data-gtm-event-label="pediatric" href="/clinical-information#trial-1-safety">pediatric</Link> clinical trials.</p>
            </div>
          )}

          <hr className={`${styles.divider} border-hcp-eggplant border-t-2 mt-10`} />

          <div className={`hcp-callout text-center my-10 ${styles.downtitration_hcp_callout}`}>
            CRENESSITY does not address cortisol deficiency. <span className="hidden tablet:inline"><br /></span>
            Patients prescribed CRENESSITY should continue taking GCs.<sup>1</sup>
          </div>

          <hr className={`${styles.divider} border-hcp-eggplant border-t-2 mb-10`} />
          <div className={styles.down_video_container}>
            <h3>GC Down-Titration</h3>
            <p className="text-hcp-charcoal">Explore GC reduction strategies, key clinical considerations, and the importance of ongoing patient monitoring.</p>
            <Video
              ref={videoRef}
              url="https://player.vimeo.com/progressive_redirect/playback/1132561952/rendition/1080p/file.mp4?loc=external&signature=4f72fb1a895ab57bbc5bcf65576700e75945825f6175d80e655176346a666f8d"
              title="KOL Video"
              poster={getOptimizedImageUrl("/images/posters/hcp/video-gc-down-titration.jpg", 1684)}
              gtmEventCategory="Video"
              gtmEventAction="Start"
              gtmEventLabel="GC Down-Titration Video Play/Pause Video"
              id="kol-video"
              autoplay={true}
              onVideoPause={() => setVideoPlaying(false)}
              onVideoPlay={() => setVideoPlaying(true)}
              caption="/images/page/hcp/videos/captions/CRENESSITY_GC-DOWN-TITRATION.vtt"
            />
            <span className="text-hcp-charcoal text-sm">These physicians have been compensated by Neurocrine for sharing their views.</span>
            {showTranscript ?
              <div className={styles.content_transcript}>
                <div className={styles.transcript}>{transcript}</div>
                <div className={styles.button_wrapper}>
                  <Button
                    type="button"
                    variant="outline--eggplant"
                    onClick={handleReadTranscript}
                    customClass={styles.down_video_button}
                    data-gtm-event-category="Action"
                    data-gtm-event-action="Click"
                    data-gtm-event-label="Close video transcript - GC Down-Titration"
                  >Close video transcript</Button>
                </div>
              </div>
              : <Button
                  variant="outline--eggplant"
                  onClick={handleReadTranscript}
                  customClass={styles.down_video_button}
                  data-gtm-event-category="Action"
                  data-gtm-event-action="Click"
                  data-gtm-event-label="Read video transcript - GC Down-Titration"
                >Read video transcript </Button>
            }
          </div>

          <h3 className="tablet:!text-xl">Consider mineralocorticoid support during GC down-titration</h3> {/* text-xl is 24px but figma shows 25px; assuming the 1px variation is a figma mistake */}
          <p className="text-hcp-charcoal">Some GCs—especially hydrocortisone—have mineralocorticoid effects. As GC doses are lowered, mineralocorticoid activity may also decrease. This can increase the need for fludrocortisone supplementation.<sup>7</sup></p>

          <div className={styles.down_purple_container}>
            <p><strong className="text-hcp-charcoal">To guide mineralocorticoid adjustments, monitor<sup className="!font-normal">8,9</sup>:</strong></p>
            <ul>
              <li>Plasma renin levels</li>
              <li>Blood pressure</li>
              <li>Electrolyte levels</li>
            </ul>
          </div>

          <div className={styles.down_btn_container}>
            <Button
              variant="outline--eggplant"
              customClass={styles.down_btn}
              onClick={handleGcDoseModal}
              data-gtm-event-category="Internal Link"
              data-gtm-event-action="Click"
              data-gtm-event-label="GC dose reductions in clinical trials">
                GC dose reductions in clinical trials</Button>
            <Button
              variant="outline--eggplant"
              iconType="download-eggplant"
              customClass={styles.down_btn}
              href="/pdfs/crenessity-patient-treatment-goal-case-studies.pdf"
              targetBlank
              data-gtm-event-category="PDF"
              data-gtm-event-action="Download"
              data-gtm-event-label="GC down-titration examples"
            >
              GC down-titration examples
            </Button>
            <Button
              variant="outline--eggplant"
              customClass={styles.down_btn}
              onClick={handleStressDosingModal}
              data-gtm-event-category="Internal Link"
              data-gtm-event-action="Click"
              data-gtm-event-label="Stress dosing considerations"
            >
              Stress dosing considerations
            </Button>
          </div>
        </div>

        <div className={styles.down_sticky}>
          <div className={styles.down_yellow_block}>
            <CustomImage
              src="/images/components/Dosing_Support-Icon.png"
              width={302}
              height={143}
              alt=""
            />
            <p><strong>Every patient&rsquo;s experience is different.</strong> Encourage ongoing dialogue as you adjust GC dosing alongside CRENESSITY treatment.<sup>6</sup></p>
          </div>
        </div>
      </div>
    </section>
    
    <Modal ref={gcDoseModalRef} customClass={styles.dose_modal} onClose={closeAllModals}>
      <div>
        <h2>GC dose reductions during CRENESSITY clinical trials<sup>1,2,10-14</sup></h2>
        <p className={styles.dose_intro}>Patients maintained their GC regimens for the first 4 weeks in CAHtalyst<sup>&reg;</sup> Pediatric and CAHtalyst<sup>&reg;</sup> Adult.</p>
        <div className={styles.dose_list}>
          <div className={styles.dose_section}>
            <h3>CAHtalyst<sup>&reg;</sup> Pediatric details</h3>
            <ul>
              <li>In CAHtalyst<sup>&reg;</sup> Pediatric, after the initial 4 weeks, GC doses were reduced in &le;4 steps to reach a more physiologic range <span className="text-nowrap">(8&ndash;10&nbsp;mg/m<sup>2</sup>/day)</span> provided androstenedione levels were maintained or improved*</li>
              <li>For patients who switched to CRENESSITY in the open-label period of CAHtalyst<sup>&reg;</sup> Pediatric, GC doses were reduced approximately monthly from month 8 to month 12 to reach a more physiologic range <span className="text-nowrap">(8&ndash;10&nbsp;mg/m<sup>2</sup>/day)</span></li>
            </ul>
          </div>
          <div className={styles.dose_section}>
            <h3>CAHtalyst<sup>&reg;</sup> Adult details</h3>
            <ul>
              <li>In CAHtalyst<sup>&reg;</sup> Adult, after the initial 4 weeks of the double-blind period, GC doses were reduced in &le;4 steps to reach a more physiologic range <span className="text-nowrap">(8&ndash;10&nbsp;mg/m<sup>2</sup>/day)</span>; from weeks 12 to 20, the GC dose was further adjusted with the goal of achieving the lowest tolerated dose while maintaining or improving androstenedione levels*</li>
              <li>For patients who switched to CRENESSITY in the open-label period of CAHtalyst<sup>&reg;</sup> Adult, GC doses were reduced approximately monthly from month 7 to month 10 to reach a more physiologic range <span className="text-nowrap">(8&ndash;10&nbsp;mg/m<sup>2</sup>/day)</span></li>
            </ul>
          </div>
        </div>
        <p className={styles.dose_nonphysiologic}>When possible, regimens were adjusted to <strong>remove the most nonphysiologic GC</strong> types (eg, dexamethasone, prednisone) or doses (ie, nighttime doses).</p>
        <div className={styles.dose_footnote}>
          <p><span className="hang">*</span>In CAHtalyst<sup>&reg;</sup> Pediatric, maintaining or improving androstenedione levels was defined as ≤120% of the baseline value or ≤ULN for sex and either age (for Tanner stage 1) or pubertal stage (for Tanner stages 2-5) for the visit. In CAHtalyst<sup>&reg;</sup> Adult, it was defined as ≤120% of the baseline value or ≤ULN for age and sex.<sup>11</sup></p>
        </div>
      </div>
    </Modal>
    
    <Modal ref={stressDosingModalRef} customClass={styles.stress_dosing_modal} onClose={closeAllModals}>
      <div className={styles.stress_dosing_content}>
        <h2 className={styles.stress_dosing_content_narrow}>Clinical considerations for stress dosing with CRENESSITY</h2>
        <p className={styles.stress_dosing_content_narrow}>As your patient taking CRENESSITY reduces their GC dose, it&rsquo;s important to clarify how and when they should stress dose. While CRENESSITY may allow for reductions in maintenance GC, reducing the GC dose doesn&rsquo;t change how much cortisol the body needs during times of stress. Here are some considerations you may need to discuss with your patients.<sup>1,15-17</sup></p>
        <div className={styles.carousel_container} style={{ height: `${carouselHeight}`}}>
          <div 
            ref={carouselRef}
            className={styles.carousel}
            style={{ transform: `translateX(-${slideNumber * 25}%)` }}
          >
            <div className={styles.carousel_slide}>
              <div className={styles.carousel_slide_block}>
                <h3>Review and revise stress dosing protocols following GC dose reductions.</h3>
                <p>Standard instructions (eg, “double” or “triple” the dose) may be insufficient if the patient&rsquo;s maintenance GC dose has been reduced after starting CRENESSITY. Stress dosing remains necessary and should be based on cortisol needs.<sup>15-17</sup></p>
              </div>
            </div>
            <div className={styles.carousel_slide}>
              <div className={styles.carousel_slide_block}>
                <h3>Ensure appropriate dosing frequency during stress.<sup>15-17</sup></h3>
                <p>During stress dosing, hydrocortisone should be given in <strong>3-4 divided doses per day</strong>, even if the maintenance dosing frequency has been reduced with CRENESSITY.</p>
                <p>For example, a patient on 2 doses/day for maintenance may need to increase the frequency to 3-4 doses/day and the total amount during illness.</p>
              </div>
            </div>
            <div className={styles.carousel_slide}>
              <div className={styles.carousel_slide_block}>
                <h3>Prescribe GCs based on absolute GC stress dose, not proportional increases.<sup>15-17</sup></h3>
                <p>Typical recommended <strong>hydrocortisone stress dose: 30-40 mg/m²/day</strong>. </p>
                <p>As an example:</p>
                <p><strong>Before CRENESSITY:</strong> Original maintenance dose = 30 mg/day --&gt; Stress dose = 60&nbsp;mg/day (doubling maintenance dose to 60 mg matches stress dose).</p>
                <p><strong>After CRENESSITY:</strong> Reduced maintenance dose = 17.5 mg/day --&gt; Stress dose = 60&nbsp;mg/day (doubling this maintenance dose to 35 mg would be <strong>inadequate</strong> for stress needs).</p>
              </div>
            </div>
            <div className={styles.carousel_slide}>
              <div className={styles.carousel_slide_block}>
                <h3>Reassess the threshold for initiating stress dosing.<sup>18</sup></h3>
                <p>Patients previously on high maintenance GCs may not have needed stress dosing for mild illnesses.</p>
                <p>After GC dose reduction with CRENESSITY, lower thresholds for stress dosing may be appropriate.</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.carousel_controls}>
          <Button 
          type = 'button'
            variant={`${slideNumber === 0 ? 'outline--eggplant' : 'solid--eggplant'}`}
            customClass={styles.carousel_prev} 
            onClick={() => handleSlideChange(slideNumber - 1)} 
            disabled={slideNumber === 0}
            data-gtm-event-category="Action"
            data-gtm-event-action="Click"
            data-gtm-event-label="Previous - clinical considertions"
          >
            <span>Previous <span>consideration</span></span>
          </Button>
          <Button 
            type = 'button'
            variant={`${slideNumber === 3 ? 'outline--eggplant' : 'solid--eggplant'}`}
            customClass={styles.carousel_next} 
            onClick={() => handleSlideChange(slideNumber + 1)} 
            disabled={slideNumber === 3}
            data-gtm-event-category="Action"
            data-gtm-event-action="Click"
            data-gtm-event-label="Next - clinical considerations"
          >
            <span>Next <span>consideration</span></span>
          </Button>
        </div>

        <div className={styles.carousel_indicators}>
          {Array.from({ length: 4 }).map((_, index) => (
            <button 
              key={index} 
              className={`${styles.carousel_indicator} ${slideNumber === index ? styles.active : ''}`} 
              onClick={() => handleSlideChange(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={slideNumber === index ? 'true' : 'false'}
              gtm-event-category="Action"
              gtm-event-action="Click"
              gtm-event-label={`Clinical considerations slide ${index + 1} bar`}
            />
          ))}
        </div>
      </div>
    </Modal>
  </>
  );
}
